/**
 * Variant Details Handler
 * Updates USP content based on variant metafields when variant changes
 */
(function () {
    'use strict';

    class VariantDetailsHandler {
        constructor() {
            this.uspContainers = [];
            this.init();
        }

        init() {
            // Wait for DOM to be ready
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.setup());
            } else {
                this.setup();
            }
        }

        setup() {
            // Find all USP containers with metafield enabled
            this.uspContainers = Array.from(document.querySelectorAll('.product-form__usps[data-enable-metafield="true"]'));

            if (this.uspContainers.length === 0) {
                return; // No USP containers with metafield enabled
            }

            // Initialize each container
            this.uspContainers.forEach(container => {
                this.initContainer(container);
                // Hide container initially if metafield is enabled (will show if variant has data)
                const uspsContainer = container.querySelector('.usps-container');
                if (uspsContainer) {
                    uspsContainer.style.display = 'none';
                }
            });

            // Listen for variant changes on all product forms
            document.addEventListener('variant:changed', (event) => {
                this.handleVariantChange(event);
            });

            // Also listen for form-specific variant changes
            this.uspContainers.forEach(container => {
                const formId = container.getAttribute('data-form-id');
                if (formId) {
                    const form = document.getElementById(formId);
                    if (form) {
                        form.addEventListener('variant:changed', (event) => {
                            this.handleVariantChange(event, container);
                        });
                    }
                }
            });

            // Handle initial variant on page load (with a small delay to ensure form is initialized)
            setTimeout(() => {
                this.updateInitialVariants();
            }, 500);
        }

        updateInitialVariants() {
            this.uspContainers.forEach(container => {
                const formId = container.getAttribute('data-form-id');
                if (formId) {
                    const form = document.getElementById(formId);
                    if (form) {
                        let variantId = null;
                        let variant = null;

                        // First, check URL parameter for variant ID
                        const urlParams = new URLSearchParams(window.location.search);
                        const urlVariantId = urlParams.get('variant');
                        if (urlVariantId) {
                            variantId = parseInt(urlVariantId);
                        }

                        // If no URL variant, try to get from form input
                        if (!variantId) {
                            const variantInput = form.querySelector('input[name="id"]');
                            if (variantInput && variantInput.value) {
                                variantId = parseInt(variantInput.value);
                            }
                        }

                        // Try to get variant object from product-variants element
                        const productVariants = form.closest('.product-form')?.querySelector('product-variants');
                        // The theme getter calls _getVariantById(), which assumes product JSON has finished
                        // loading. App blocks can delay that data beyond this handler's 500 ms timer.
                        if (productVariants && productVariants.product) {
                            if (productVariants.selectedVariant) {
                                variant = productVariants.selectedVariant;
                                // Use variant ID from variant object if we don't have one
                                if (!variantId && variant.id) {
                                    variantId = variant.id;
                                }
                            } else if (variantId && productVariants.product) {
                                // Try to find variant by ID in product variants
                                const allVariants = productVariants.product.variants || [];
                                variant = allVariants.find(v => v.id === variantId);
                            }
                        }

                        if (variantId && variant) {
                            this.updateUSPContent(container, variantId, variant);
                        } else if (variantId) {
                            // If we have variant ID but no variant object, still try to update
                            // The matching will happen based on variant_trigger_name
                            this.updateUSPContent(container, variantId, null);
                        }
                    }
                }
            });
        }

        initContainer(container) {
            try {
                // Parse default content
                const defaultContentStr = container.getAttribute('data-default-content');
                if (defaultContentStr) {
                    try {
                        container.defaultContent = JSON.parse(defaultContentStr);
                    } catch (e) {
                        // Silent error handling
                    }
                }

                // Parse variant metafields
                const variantMetafieldsStr = container.getAttribute('data-variant-metafields');
                if (variantMetafieldsStr) {
                    try {
                        // Clean the string - remove any potential HTML entities or extra characters
                        const cleanedStr = variantMetafieldsStr.trim().replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
                        container.variantMetafields = JSON.parse(cleanedStr);
                    } catch (e) {
                        // Set to empty object to prevent further errors
                        container.variantMetafields = {};
                    }
                }

                // Load rich text HTML for ups_short_description from hidden divs
                const blockId = container.getAttribute('data-block-id');
                // Try multiple methods to find the container
                let richTextContainer = null;
                const possibleIds = [
                    'variant-usps-rich-text-{{ section.id }}-' + blockId,
                    'variant-usps-rich-text-' + container.closest('section')?.id + '-' + blockId,
                    'variant-usps-rich-text-' + document.querySelector('.product-form')?.closest('section')?.id + '-' + blockId
                ];

                // Also try querySelector as fallback
                const allContainers = document.querySelectorAll('[id^="variant-usps-rich-text-"]');
                if (allContainers.length > 0) {
                    // Find container with matching block ID
                    for (const cont of allContainers) {
                        const contBlockId = cont.getAttribute('data-block-id');
                        if (contBlockId === blockId) {
                            richTextContainer = cont;
                            break;
                        }
                    }
                }

                // If still not found, try by ID
                if (!richTextContainer) {
                    for (const id of possibleIds) {
                        richTextContainer = document.getElementById(id);
                        if (richTextContainer) break;
                    }
                }

                if (richTextContainer) {
                    const richTextDivs = richTextContainer.querySelectorAll('[data-variant-id][data-ups-description-content]');

                    richTextDivs.forEach(div => {
                        const variantId = div.getAttribute('data-variant-id');
                        const htmlContent = div.innerHTML.trim();

                        // Initialize variant entry if it doesn't exist
                        if (!container.variantMetafields[variantId]) {
                            container.variantMetafields[variantId] = {};
                        }

                        if (htmlContent) {
                            container.variantMetafields[variantId].ups_short_description = htmlContent;
                        }
                    });
                }
            } catch (error) {
                // Silent error handling
            }
        }

        handleVariantChange(event, specificContainer = null) {
            const variant = event.detail?.variant;
            if (!variant || !variant.id) {
                return;
            }

            const containers = specificContainer ? [specificContainer] : this.uspContainers;

            containers.forEach(container => {
                if (container.getAttribute('data-enable-metafield') !== 'true') {
                    return; // Skip if metafield is not enabled for this container
                }

                this.updateUSPContent(container, variant.id, variant);
            });
        }

        updateUSPContent(container, variantId, variant = null) {
            if (!container.variantMetafields) {
                return;
            }

            const uspsList = container.querySelector('[data-usps-list]');
            const descriptionEl = container.querySelector('[data-usps-description]');
            const uspsContainer = container.querySelector('.usps-container');

            if (!uspsList) {
                return;
            }

            // If we have variant object, use it for matching
            // Otherwise, we'll search all metafields for matching trigger names
            let matchedVariantData = null;

            if (variant) {
                // Get selected variant's title and options
                const variantTitle = variant.title || '';
                const variantOptions = variant.options || [];

                // Normalize selected variant values for comparison
                const normalizedTitle = String(variantTitle).trim().toLowerCase();
                const normalizedOptions = variantOptions.map(opt => String(opt).trim().toLowerCase());

                // Search through all variants' metafields to find a match
                for (const [vid, variantData] of Object.entries(container.variantMetafields)) {
                    if (!variantData || !variantData.variant_trigger_name) {
                        continue;
                    }

                    const variantTriggerName = String(variantData.variant_trigger_name).trim().toLowerCase();

                    // Check if trigger name matches selected variant's title
                    if (variantTriggerName === normalizedTitle) {
                        matchedVariantData = variantData;
                        break;
                    }

                    // Check if trigger name matches any of the selected variant's options
                    for (let i = 0; i < normalizedOptions.length; i++) {
                        if (variantTriggerName === normalizedOptions[i]) {
                            matchedVariantData = variantData;
                            break;
                        }
                    }

                    if (matchedVariantData) {
                        break;
                    }
                }
            } else {
                // If no variant object, check if the variantId exists in metafields
                // This handles the case where variant is selected via URL parameter
                const variantData = container.variantMetafields[variantId];
                if (variantData && variantData.variant_trigger_name) {
                    // We have metafield data for this variant ID
                    // But we still need to check if trigger name matches
                    // For now, if variant ID matches, use it
                    matchedVariantData = variantData;
                }
            }

            // Check if matched variant has metafield data (at least one complete item with a title)
            const hasMetafieldData = matchedVariantData &&
                (matchedVariantData.ups_title_1 || matchedVariantData.ups_title_2 ||
                    matchedVariantData.ups_title_3 || matchedVariantData.ups_title_4);

            // Check if we should show default content when metafield is blank
            const enableWhenBlank = container.getAttribute('data-enable-when-metafield-blank') === 'true';

            // If no match found OR metafield data is blank
            if (!hasMetafieldData) {
                if (enableWhenBlank) {
                    // Show default content
                    if (uspsContainer) {
                        uspsContainer.style.display = '';
                    }
                    if (container.defaultContent) {
                        const itemsToRender = container.defaultContent.items || [];
                        this.renderUSPList(uspsList, itemsToRender);
                        if (descriptionEl) {
                            const defaultDesc = container.defaultContent.description || '';
                            if (defaultDesc && defaultDesc !== 'null' && defaultDesc !== 'undefined' && defaultDesc.trim() !== '') {
                                descriptionEl.innerHTML = defaultDesc;
                                descriptionEl.style.display = '';
                            } else {
                                descriptionEl.style.display = 'none';
                            }
                        }
                    }
                } else {
                    // Hide the section
                    if (uspsContainer) {
                        uspsContainer.style.display = 'none';
                    }
                }
                return;
            }

            // Show the container if it was hidden
            if (uspsContainer) {
                uspsContainer.style.display = '';
            }

            // Use matched metafield data
            const itemsToRender = this.buildUSPItemsFromMetafield(matchedVariantData);
            // Handle ups_short_description - could be HTML string or AST object
            let description = '';
            if (matchedVariantData.ups_short_description) {
                const descValue = matchedVariantData.ups_short_description;
                // If it's already HTML (string starting with <), use it directly
                if (typeof descValue === 'string' && descValue.trim().startsWith('<')) {
                    description = descValue;
                }
                // If it's an object (AST), convert it to HTML
                else if (typeof descValue === 'object' && descValue !== null) {
                    description = this.convertRichTextToHTML(descValue);
                }
                // If it's a string that might be JSON, try to parse and convert
                else if (typeof descValue === 'string') {
                    description = this.convertRichTextToHTML(descValue);
                }
            }

            // Update the list
            this.renderUSPList(uspsList, itemsToRender);

            // Update description
            if (descriptionEl) {
                if (description && description !== 'null' && description !== 'undefined' && description.trim() !== '') {
                    // Handle both plain text and HTML (rich text)
                    descriptionEl.innerHTML = description;
                    descriptionEl.style.display = '';
                } else {
                    descriptionEl.style.display = 'none';
                }
            }
        }

        convertRichTextToHTML(richText) {
            if (!richText) {
                return '';
            }

            // If it's already a string, check if it's a JSON string or HTML
            if (typeof richText === 'string') {
                // Try to parse if it looks like JSON (starts with { or [)
                if ((richText.trim().startsWith('{') || richText.trim().startsWith('[')) && !richText.trim().startsWith('<')) {
                    try {
                        const parsed = JSON.parse(richText);
                        // If parsed successfully and has rich text structure, convert it
                        if (parsed && typeof parsed === 'object' && parsed.type === 'root') {
                            return this.parseRichTextAST(parsed);
                        }
                        return String(parsed);
                    } catch (e) {
                        // If parsing fails, it might be HTML, return as is
                        return richText;
                    }
                }
                // If it's HTML string, return it
                return richText;
            }

            // If it's an object with the rich text AST structure
            if (typeof richText === 'object' && richText !== null) {
                // Check if it has the Shopify rich text structure
                if (richText.type === 'root' && Array.isArray(richText.children)) {
                    return this.parseRichTextAST(richText);
                }
                // Try common properties
                if (richText.value) {
                    return String(richText.value);
                }
                if (richText.html) {
                    return String(richText.html);
                }
            }

            return String(richText);
        }

        parseRichTextAST(node) {
            if (!node || typeof node !== 'object') {
                return '';
            }

            let html = '';

            // Handle root node
            if (node.type === 'root' && Array.isArray(node.children)) {
                html = node.children.map(child => this.parseRichTextAST(child)).join('');
            }
            // Handle paragraph
            else if (node.type === 'paragraph' && Array.isArray(node.children)) {
                html = '<p>' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</p>';
            }
            // Handle text
            else if (node.type === 'text' && node.value) {
                html = this.escapeHtml(node.value);
            }
            // Handle bold
            else if (node.type === 'bold' && Array.isArray(node.children)) {
                html = '<strong>' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</strong>';
            }
            // Handle italic
            else if (node.type === 'italic' && Array.isArray(node.children)) {
                html = '<em>' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</em>';
            }
            // Handle link
            else if (node.type === 'link' && Array.isArray(node.children)) {
                const url = node.url || node.href || '#';
                html = '<a href="' + this.escapeHtml(url) + '">' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</a>';
            }
            // Handle line break
            else if (node.type === 'line-break') {
                html = '<br>';
            }
            // Handle list
            else if (node.type === 'list' && Array.isArray(node.children)) {
                const tag = node.listType === 'ordered' ? 'ol' : 'ul';
                html = '<' + tag + '>' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</' + tag + '>';
            }
            // Handle list item
            else if (node.type === 'list-item' && Array.isArray(node.children)) {
                html = '<li>' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</li>';
            }
            // Handle heading
            else if (node.type && node.type.startsWith('heading-') && Array.isArray(node.children)) {
                const level = node.type.replace('heading-', '') || '1';
                html = '<h' + level + '>' + node.children.map(child => this.parseRichTextAST(child)).join('') + '</h' + level + '>';
            }
            // Fallback: if it has children, process them
            else if (Array.isArray(node.children)) {
                html = node.children.map(child => this.parseRichTextAST(child)).join('');
            }

            return html;
        }

        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }

        buildUSPItemsFromMetafield(variantData) {
            const items = [];

            // Build items from metafield data (4 items max based on the schema)
            for (let i = 1; i <= 4; i++) {
                const iconTitle = variantData[`icon_title_${i}`];
                const iconText = variantData[`icon_text_${i}`];
                const upsTitle = variantData[`ups_title_${i}`];

                // Only add if we have at least a title
                if (upsTitle) {
                    items.push({
                        icon: iconTitle || null,
                        icon_text: iconText || '',
                        label: upsTitle,
                        has_icon: !!iconTitle
                    });
                }
            }

            return items;
        }

        renderUSPList(uspsList, items) {
            // Clear existing items
            uspsList.innerHTML = '';

            if (items.length === 0) {
                return;
            }

            // Create new items
            items.forEach((item, index) => {
                const li = document.createElement('li');
                li.className = 'usp-item';
                li.setAttribute('data-usp-index', index + 1);

                const iconSpan = document.createElement('span');
                iconSpan.className = 'usp-icon';

                if (item.has_icon && item.icon) {
                    // Use image icon
                    const img = document.createElement('img');
                    img.src = item.icon;
                    img.alt = item.label;
                    img.width = 24;
                    img.height = 24;
                    img.setAttribute('data-usp-icon-img', '');
                    iconSpan.appendChild(img);
                } else if (item.icon_text) {
                    // Use text icon
                    iconSpan.textContent = item.icon_text;
                    iconSpan.setAttribute('data-usp-icon-text', '');
                }

                const labelSpan = document.createElement('span');
                labelSpan.className = 'usp-label';
                labelSpan.textContent = item.label;
                labelSpan.setAttribute('data-usp-label', '');

                li.appendChild(iconSpan);
                li.appendChild(labelSpan);
                uspsList.appendChild(li);
            });
        }
    }

    // Initialize the handler
    new VariantDetailsHandler();
})();
